import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Printer } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { downloadBlob } from "@/utils/download";
import { useDrawing, useResults, useTechCalcProject } from "../hooks";
import { drawingPoints, extractApiError } from "../utils";
import { TechCalcCardDocument } from "../components/TechCalcCardDocument";
import { downloadCardPdf } from "../cardsZip";

/**
 * Karta obliczeń pojedynczego słupa — podgląd A4. Przycisk „Drukuj (PDF)"
 * pobiera PDF wydrukowany przez backendowy Chromium (ten sam pipeline co
 * eksport ZIP kart) — bez nagłówków/stopek, które window.print dokleja z
 * przeglądarki (tytuł strony, URL, numer strony, data). Reguły @media print
 * zostają dla ręcznego Ctrl+P. Zawsze białe tło, niezależnie od motywu — to
 * dokument, nie ekran. Sama treść karty żyje w TechCalcCardDocument
 * (współdzielona z eksportem ZIP kart); tutaj zostały routing, toolbar i
 * reguły wydruku dotyczące shella aplikacji.
 */
export function TechCalcCardPage() {
  const { projectId, variantId, poleRef } = useParams<{
    projectId: string;
    variantId: string;
    poleRef: string;
  }>();
  const { data: project, isLoading: projectLoading } =
    useTechCalcProject(projectId);
  const { data: drawing, isLoading: drawingLoading } = useDrawing(variantId);
  const { data: resultsData } = useResults(variantId);
  const [printing, setPrinting] = useState(false);

  if (projectLoading || drawingLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }

  const variant = project?.variants.find((v) => v.id === variantId);
  const pole = drawingPoints(drawing).find((p) => p.properties.ref === poleRef);
  if (!project || !pole) {
    return (
      <p className="py-16 text-center font-mono text-xs text-[var(--color-text-muted)]">
        Nie znaleziono słupa {poleRef} w tym wariancie.{" "}
        <Link
          className="text-[var(--color-primary)] hover:underline"
          to={`/tech-calc/${projectId}`}
        >
          Wróć do projektu
        </Link>
      </p>
    );
  }

  const handlePrint = async () => {
    if (!variantId || !drawing) return;
    setPrinting(true);
    try {
      const { blob, filename } = await downloadCardPdf({
        variantId,
        projectName: project.name,
        variantName: variant?.name ?? "—",
        drawing,
        pole,
        result: resultsData?.results?.poles?.[poleRef ?? ""],
        stale: !!resultsData?.stale,
      });
      downloadBlob(blob, filename);
    } catch (err) {
      window.alert(extractApiError(err));
    } finally {
      setPrinting(false);
    }
  };

  return (
    <div>
      <style>{`
        @media print {
          /* ── PRZYCZYNA PUSTEJ 2. STRONY ──────────────────────────────────
             Karta drukuje się wewnątrz shella aplikacji (ProtectedLayout):
             div.h-[100dvh] + sidebar/topbar + main.p-6. visibility:hidden
             ukrywa chrome, ale zostawia jego WYSOKOŚĆ w przepływie — sam
             shell mierzy pełną wysokość strony, więc każdy dodatkowy piksel
             (padding maina) spychał pustą resztę na 2. kartkę. W druku:
             chrome znika z przepływu (display:none), łańcuch przodków karty
             traci sztywne wysokości/scrolle, karta (absolute, reguła w
             TechCalcCardDocument) niesie 1 stronę. */
          html, body, #root { height: auto !important; min-height: 0 !important; }
          #root nav, #root header { display: none !important; }
          #root div[class*="100dvh"],
          #root div[class*="overflow-hidden"],
          #root main {
            height: auto !important;
            min-height: 0 !important;
            max-height: none !important;
            overflow: visible !important;
          }
          #root main { padding: 0 !important; }
          body * { visibility: hidden; }
          #tc-card, #tc-card * { visibility: visible; }
          .no-print { display: none !important; }
        }
      `}</style>

      <div className="no-print mb-4 flex items-center justify-between">
        <Link
          to={`/tech-calc/${projectId}`}
          className="section-label inline-flex items-center gap-1 hover:text-[var(--color-primary)]"
        >
          ← Wróć do projektu
        </Link>
        <Button size="sm" onClick={handlePrint} disabled={printing}>
          <Printer size={14} className="mr-1" />
          {printing ? "Przygotowuję PDF…" : "Drukuj (PDF)"}
        </Button>
      </div>

      <TechCalcCardDocument
        projectName={project.name}
        variantName={variant?.name ?? "—"}
        drawing={drawing}
        pole={pole}
        result={resultsData?.results?.poles?.[poleRef ?? ""]}
        stale={!!resultsData?.stale}
      />
    </div>
  );
}
